import { z } from "zod";
import { getCookie } from "./cookie";
import { decodeJwt } from "./jwt";
import { semaphore } from "./semaphore";
import { secondsToMs } from "./time";

const AccessTokenValidator = z.object({
    sub: z.number(),
    exp: z.number(),
});

const RefreshResponseValidator = z.object({
    data: z.string(),
});

export type AuthState = {
    token: string | null | undefined;
};

export type Token = string | null | undefined;

class AuthStateBase implements AuthState {
    #token: Token;

    protected set token(value: Token) {
        this.#token = value;
        this.emitStateChange();
    }

    get token() {
        return this.#token;
    }

    private stateChangeListeners: Array<(state: AuthState) => void> = [];

    addStateChangeListener(stateChangeListener: (state: AuthState) => void) {
        this.stateChangeListeners.push(stateChangeListener);

        return () => {
            this.stateChangeListeners = this.stateChangeListeners.filter(
                (listener) => listener !== stateChangeListener,
            );
        };
    }

    emitStateChange() {
        this.stateChangeListeners.forEach((listener) => listener({ token: this.#token }));
    }
}

export default class Auth extends AuthStateBase {
    private static instance: Auth | null = null;
    private static references = 0;
    private readonly authChannel = new BroadcastChannel("AUTH_BROADCAST_CHANNEL");
    private readonly intervalId;

    static getOrCreate() {
        this.references += 1;

        if (this.instance === null) {
            this.instance = new this();
        }

        return this.instance;
    }

    private constructor() {
        super();

        setTimeout(() => {
            void this.refreshAccessToken();
        }, 0);

        this.intervalId = setInterval(() => {
            void this.refreshAccessToken();
        }, 2000);

        this.authChannel.onmessage = (event) => {
            const { type } = event.data as { type?: string };

            if (type === "LOGOUT") {
                this.token = null;
            }

            if (type === "LOGIN" && this.token === null) {
                this.token = undefined;
            }
        };
    }

    private destroy() {
        clearInterval(this.intervalId);
        this.authChannel.onmessage = null;
    }

    static clearInstance() {
        const instance = this.instance;

        if (instance !== null && --this.references <= 0) {
            instance.destroy();
            this.instance = null;
        }
    }

    async refreshAccessToken(): Promise<string | null> {
        const accessTokenFromCookie = () => {
            const accessToken = getCookie("accessToken");

            if (accessToken === null) {
                return null;
            }

            let decodedAccessToken: unknown;

            try {
                decodedAccessToken = decodeJwt(accessToken);
            } catch {
                return null;
            }

            const parsedAccessToken = AccessTokenValidator.safeParse(decodedAccessToken);

            if (!parsedAccessToken.success) {
                return null;
            }

            return { accessToken, fromCookie: true };
        };

        const tokenResult =
            accessTokenFromCookie() ??
            (await semaphore("refresh_access_token", async () => {
                return (
                    accessTokenFromCookie() ?? {
                        accessToken: await this.refreshAccessTokenUnsafe(),
                        fromCookie: false,
                    }
                );
            }));

        const { accessToken, fromCookie } = tokenResult;

        if (!fromCookie && accessToken !== null) {
            const expires = new Date(Date.now() + secondsToMs(60));
            document.cookie = `accessToken=${accessToken}; expires=${expires.toUTCString()}; path=/`;
        }

        this.token = accessToken;
        return accessToken;
    }

    private async refreshAccessTokenUnsafe(): Promise<string | null> {
        const response = await fetch("/api/auth/refresh", { keepalive: true });

        if (response.status !== 200) {
            return null;
        }

        const json = await response.json();
        return RefreshResponseValidator.parse(json).data;
    }

    async logout() {
        await fetch("/api/auth/logout", {
            headers: { Authorization: `Bearer ${this.token}` },
            credentials: "same-origin",
        });

        document.cookie = "accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        this.token = null;
        this.authChannel.postMessage({ type: "LOGOUT" });
    }
}
